<template>
  <div v-loading="refreshing" class="mobile-photos-container">
    <div class="photo-header">
      <div class="header-left">
        <span class="title">我的相册</span>
        <span v-if="isEditMode" class="selection-info">已选择 {{ selectedIds.length }} 项</span>
      </div>
      <div class="header-right">
        <el-button
          :type="isEditMode ? 'default' : 'primary'"
          size="mini"
          round
          @click="toggleEditMode"
        >
          {{ isEditMode ? '取消' : '选择' }}
        </el-button>
      </div>
    </div>

    <div
      v-infinite-scroll="onLoad"
      class="scroll-wrapper"
      :infinite-scroll-disabled="disabled"
      infinite-scroll-distance="50"
    >
      <div v-for="group in groupedPhotos" :key="group.date" class="photo-group">
        <div class="group-header">
          <span class="date-title">{{ formatGroupDate(group.date) }}</span>
          <div v-if="isEditMode" class="group-select-btn" @click="selectGroup(group)">
            {{ isGroupSelected(group) ? '取消全选' : '全选' }}
          </div>
        </div>

        <div class="photo-grid">
          <div
            v-for="item in group.children"
            :key="item.fileId"
            class="photo-item"
            @click="onItemClick(item)"
          >
            <el-image
              class="image-content"
              :src="item.fileType === 1002 ? require('@/assets/img/video.jpg') : item.url"
              fit="cover"
              lazy
            />

            <div v-if="item.fileType === 1002" class="video-badge">
              <i class="el-icon-video-play" />
              <span>{{ item.duration || '视频' }}</span>
            </div>

            <div v-if="isEditMode" class="select-mask" :class="{ 'is-active': selectedIds.includes(item.fileId) }">
              <div class="check-icon-wrapper">
                <i class="el-icon-check" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="load-status">
        <p v-if="loading" class="status-text"><i class="el-icon-loading" /> 努力加载中...</p>
        <p v-if="finished && photoList.length > 0" class="status-text">已经看完全部照片啦</p>
        <el-empty v-if="finished && photoList.length === 0" :image-size="80" description="暂无照片" />
      </div>
    </div>

    <transition name="slide-up">
      <div v-if="isEditMode" class="batch-footer-action">
        <div class="action-btn download" :class="{ disabled: selectedIds.length === 0 }" @click="handleBatchDownload">
          <i class="el-icon-download" />
          <span>下载</span>
        </div>
        <div class="action-btn delete" :class="{ disabled: selectedIds.length === 0 }" @click="handleBatchDelete">
          <i class="el-icon-delete" />
          <span>删除</span>
        </div>
      </div>
    </transition>

    <el-dialog
      :visible.sync="showPlayerModal"
      width="100%"
      fullscreen
      custom-class="mobile-video-dialog"
      append-to-body
      @closed="onVideoPopupClosed"
    >
      <div v-if="currentVideoItem" class="video-fullscreen-wrapper">
        <div class="video-header-bar">
          <i class="el-icon-arrow-left close-btn" @click="showPlayerModal = false" />
          <span class="video-title">{{ currentVideoItem.filename }}</span>
        </div>
        <div class="video-body">
          <video
            ref="videoPlayer"
            :src="currentVideoItem.videoUrl"
            controls
            playsinline
            autoplay
            webkit-playsinline
            class="mobile-video-player"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getPhotoItems } from '@/api/disk'

export default {
  name: 'DiskPhotoMobile',
  data() {
    return {
      photoList: [],
      loading: false,
      finished: false,
      refreshing: false,
      isEditMode: false,
      selectedIds: [],
      queryParams: {
        pn: 1
      },
      showPlayerModal: false,
      currentVideoItem: null
    }
  },
  computed: {
    groupedPhotos() {
      return this.groupDataByDate(this.photoList)
    },
    disabled() {
      return this.loading || this.finished
    }
  },
  methods: {
    async onLoad() {
      if (this.finished || this.loading) return
      this.loading = true

      try {
        const resp = await getPhotoItems(this.queryParams)
        if (resp.code === 0) {
          const list = resp.data.list || []
          this.photoList = [...this.photoList, ...list]

          if (!resp.data.hasNext) {
            this.finished = true
          } else {
            this.queryParams.pn++
          }
        } else {
          this.finished = true
          this.$message.error(resp.msg)
        }
      } catch (e) {
        this.finished = true
      } finally {
        this.loading = false
      }
    },

    groupDataByDate(flatList) {
      const groups = {}
      flatList.forEach(item => {
        const date = item.createAt.split(' ')[0]
        if (!groups[date]) groups[date] = []
        groups[date].push(item)
      })
      return Object.keys(groups).map(date => ({
        date,
        children: groups[date]
      })).sort((a, b) => new Date(b.date) - new Date(a.date))
    },

    formatGroupDate(dateStr) {
      const d = new Date(dateStr)
      const now = new Date()
      const today = now.toISOString().split('T')[0]
      if (dateStr === today) return '今天'

      // 昨天的简单计算
      const yesterday = new Date(now.setDate(now.getDate() - 1)).toISOString().split('T')[0]
      if (dateStr === yesterday) return '昨天'

      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
    },

    onItemClick(item) {
      if (this.isEditMode) {
        this.handleSelect(item.fileId)
      } else {
        if (item.fileType === 1001) {
          const imageUrls = []
          imageUrls.push({
            src: item.url,
            alt: item.filename
          })
          this.$viewerApi({
            images: imageUrls,
            options: {
              initialViewIndex: 0,
              movable: true,
              fullscreen: false,
              keyboard: true
            }
          })
        } else if (item.fileType === 1002) {
          this.currentVideoItem = item
          this.showPlayerModal = true
        }
      }
    },

    handleSelect(id) {
      const idx = this.selectedIds.indexOf(id)
      if (idx > -1) {
        this.selectedIds.splice(idx, 1)
      } else {
        this.selectedIds.push(id)
      }
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode
      this.selectedIds = []
    },

    selectGroup(group) {
      const groupIds = group.children.map(i => i.fileId)
      if (this.isGroupSelected(group)) {
        this.selectedIds = this.selectedIds.filter(id => !groupIds.includes(id))
      } else {
        groupIds.forEach(id => {
          if (!this.selectedIds.includes(id)) this.selectedIds.push(id)
        })
      }
    },

    isGroupSelected(group) {
      return group.children.every(i => this.selectedIds.includes(i.fileId))
    },

    handleBatchDownload() {
      if (this.selectedIds.length === 0) return
      this.$message.success(`开始下载 ${this.selectedIds.length} 项`)
    },

    handleBatchDelete() {
      if (this.selectedIds.length === 0) return
      this.$confirm(`确定删除选中的 ${this.selectedIds.length} 张照片吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: 'mobile-message-box', // 需配合全局样式微调弹窗大小
        center: true
      }).then(() => {
        this.$message.success('删除成功')
        // 实际开发中在此处调用接口更新列表
      }).catch(() => {})
    },

    onVideoPopupClosed() {
      if (this.$refs.videoPlayer) this.$refs.videoPlayer.pause()
    }
  }
}
</script>

<style scoped>
.mobile-photos-container {
  padding: 12px 4px 60px 4px; /* 底部预留操作栏空间 */
  background: #f8f9fa;
  min-height: calc(100vh - 100px);
}

/* 顶部轻量级 Title 栏 */
.photo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
}
.photo-header .title { font-size: 18px; font-weight: bold; color: #1a1a1a; }
.photo-header .selection-info { margin-left: 10px; color: #1a73e8; font-size: 13px; font-weight: bold; }

/* 日期分组头 */
.photo-group {
  margin-bottom: 16px;
}
.group-header {
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.group-header .date-title { font-size: 14px; font-weight: bold; color: #333; }
.group-header .group-select-btn { font-size: 13px; color: #1a73e8; cursor: pointer; }

/* 标准移动端相册：无缝隙 4 列矩阵 */
.photo-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* 牢牢固定 4 列 */
  gap: 2px; /* 类似系统相册的极微小拼缝 */
}

.photo-item {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1; /* 严格正方形 */
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}

.image-content {
  width: 100%;
  height: 100%;
}

/* 视频标记适配小格 */
.video-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 10px;
  display: flex;
  align-items: center;
}
.video-badge i { margin-right: 2px; font-size: 11px; }

/* 移动端多选高亮蒙层：不再是一个小圆圈，而是全屏高亮+右上角打勾 */
.select-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 6px;
  box-sizing: border-box;
}

.check-icon-wrapper {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.check-icon-wrapper i { color: transparent; font-size: 12px; font-weight: bold; }

/* 被选中时的多选状态 */
.select-mask.is-active {
  background: rgba(0, 0, 0, 0.1);
  border: 2px solid #1a73e8; /* 蓝色外框 */
}
.select-mask.is-active .check-icon-wrapper {
  background: #1a73e8;
  border-color: #1a73e8;
}
.select-mask.is-active .check-icon-wrapper i {
  color: #fff;
}

/* 底部状态 */
.load-status { text-align: center; padding: 20px 0; }
.status-text { font-size: 12px; color: #999; }

/* 移动端底部原生操作面板 */
.batch-footer-action {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 54px;
  background: #ffffff;
  border-top: 1px solid #eee;
  display: flex;
  z-index: 100;
  padding-bottom: env(safe-area-inset-bottom); /* 兼容全面屏 */
}

.action-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #333;
  font-size: 11px;
}
.action-btn i { font-size: 20px; margin-bottom: 2px; }
.action-btn.delete { color: #f56c6c; }
.action-btn.disabled { opacity: 0.3; pointer-events: none; }

/* 全屏视频播放器样式 */
::v-deep .mobile-video-dialog {
  background: #000 !important; /* 手机播放视频必须全黑背景 */
  margin: 0 !important;
}
::v-deep .mobile-video-dialog .el-dialog__header { display: none; } /* 隐去官方头 */
::v-deep .mobile-video-dialog .el-dialog__body { padding: 0; height: 100vh; }

.video-fullscreen-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  color: #fff;
}

.video-header-bar {
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.6), transparent);
  z-index: 10;
}
.video-header-bar .close-btn { font-size: 22px; margin-right: 15px; }
.video-header-bar .video-title { font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.video-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
}
.mobile-video-player {
  width: 100%;
  max-height: 80vh;
}

/* 底部滑出动画 */
.slide-up-enter-active, .slide-up-leave-active { transition: transform 0.25s ease; }
.slide-up-enter, .slide-up-leave-to { transform: translateY(100%); }
</style>
